import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';
import { 
  Play, 
  ArrowClockwise, 
  CheckCircle, 
  Clock, 
  Warning,
  Gear,
  EnvelopeSimple,
  VideoCamera,
  Image,
  Lightning,
  FilmSlate
} from '@phosphor-icons/react';

const AdminOperationsPage = () => {
  const [queueStatus, setQueueStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dripStats, setDripStats] = useState(null);
  const [runwayStatus, setRunwayStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [pexelsKey, setPexelsKey] = useState('');
  
  const fetchData = useCallback(async () => {
    try {
      const [queueRes, settingsRes, dripRes] = await Promise.all([
        adminAPI.getQueueStatus(),
        adminAPI.getSettings(),
        adminAPI.getDripStats()
      ]);
      setQueueStatus(queueRes.data);
      setSettings(settingsRes.data);
      setDripStats(dripRes.data);
    } catch (error) {
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRunwayStatus = useCallback(async () => {
    try {
      const res = await adminAPI.getRunwayStatus();
      setRunwayStatus(res.data);
    } catch (err) {
      // Non-fatal: the card will show an "unavailable" state
      setRunwayStatus((prev) => prev || { health: 'unavailable', health_message: 'Could not reach Runway API' });
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchRunwayStatus();
    // Auto-refresh ops data every 30 seconds
    const interval = setInterval(fetchData, 30000);
    // Runway status auto-refreshes every 60 seconds (per spec)
    const runwayInterval = setInterval(fetchRunwayStatus, 60000);
    return () => {
      clearInterval(interval);
      clearInterval(runwayInterval);
    };
  }, [fetchData, fetchRunwayStatus]);

  const handleProcessQueue = async () => {
    setActionLoading(prev => ({ ...prev, processQueue: true }));
    try {
      await adminAPI.processQueueNow();
      toast.success('Queue processing started');
      setTimeout(fetchData, 2000); // Refresh after 2 seconds
    } catch (error) {
      toast.error('Failed to process queue');
    } finally {
      setActionLoading(prev => ({ ...prev, processQueue: false }));
    }
  };

  const handleGenerateDaily = async () => {
    setActionLoading(prev => ({ ...prev, generateDaily: true }));
    try {
      const res = await adminAPI.generateDailyContent();
      toast.success(`Content queued for ${res.data.date}`);
      setTimeout(fetchData, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setActionLoading(prev => ({ ...prev, generateDaily: false }));
    }
  };

  const handleSaveSettings = async () => {
    if (!pexelsKey.trim()) {
      toast.error('Please enter a Pexels API key');
      return;
    }
    setActionLoading(prev => ({ ...prev, saveSettings: true }));
    try {
      await adminAPI.updateSettings({ pexels_api_key: pexelsKey });
      toast.success('Settings saved');
      setPexelsKey('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setActionLoading(prev => ({ ...prev, saveSettings: false }));
    }
  };

  const handleRunDripCampaigns = async () => {
    setActionLoading(prev => ({ ...prev, runDrip: true }));
    try {
      const res = await adminAPI.runDripCampaigns();
      toast.success(`Sent ${res.data.total_sent} emails`);
      fetchData();
    } catch (error) {
      toast.error('Failed to run drip campaigns');
    } finally {
      setActionLoading(prev => ({ ...prev, runDrip: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="operations-title">
            Daily Content Operations
          </h1>
          <p className="text-slate-600">Monitor and control your automated content system</p>
        </div>

        {/* Runway Status Card */}
        <RunwayStatusCard status={runwayStatus} onRefresh={fetchRunwayStatus} />

        {/* Queue Status Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="queue-status-section">
          <div className="p-4 md:p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Lightning size={20} weight="duotone" className="text-amber-500" />
                Queue Status
              </h2>
              <button 
                onClick={fetchData}
                className="text-slate-500 hover:text-slate-700 p-2"
                title="Refresh"
              >
                <ArrowClockwise size={20} />
              </button>
            </div>
          </div>
          
          <div className="p-4 md:p-6">
            {/* Video Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard 
                label="Pending Videos"
                value={queueStatus?.videos?.pending || 0}
                icon={<Clock size={18} className="text-amber-500" />}
                color="amber"
              />
              <StatCard 
                label="Processing"
                value={queueStatus?.videos?.processing || 0}
                icon={<ArrowClockwise size={18} className="text-blue-500" />}
                color="blue"
              />
              <StatCard 
                label="Completed"
                value={queueStatus?.videos?.completed || 0}
                icon={<CheckCircle size={18} className="text-emerald-500" />}
                color="emerald"
              />
              <StatCard 
                label="Failed"
                value={queueStatus?.videos?.failed || 0}
                icon={<Warning size={18} className="text-red-500" />}
                color="red"
              />
            </div>

            {/* Story Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard 
                label="Pending Stories"
                value={queueStatus?.stories?.pending || 0}
                icon={<Clock size={18} className="text-amber-500" />}
                color="amber"
              />
              <StatCard 
                label="Processing"
                value={queueStatus?.stories?.processing || 0}
                icon={<ArrowClockwise size={18} className="text-blue-500" />}
                color="blue"
              />
              <StatCard 
                label="Completed"
                value={queueStatus?.stories?.completed || 0}
                icon={<CheckCircle size={18} className="text-emerald-500" />}
                color="emerald"
              />
              <StatCard 
                label="Failed"
                value={queueStatus?.stories?.failed || 0}
                icon={<Warning size={18} className="text-red-500" />}
                color="red"
              />
            </div>

            {/* Scheduler Status */}
            {queueStatus?.scheduler && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${queueStatus.scheduler.running ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm font-medium text-slate-700">
                    Scheduler {queueStatus.scheduler.running ? 'Running' : 'Stopped'}
                  </span>
                </div>
                {queueStatus.scheduler.jobs?.length > 0 && (
                  <div className="space-y-2">
                    {queueStatus.scheduler.jobs.map((job) => (
                      <div key={job.id} className="flex justify-between text-sm">
                        <span className="text-slate-600">{job.name}</span>
                        <span className="text-slate-500 font-mono text-xs">
                          {job.next_run_human}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Last Generation */}
            {queueStatus?.last_daily_generation && (
              <div className="text-sm text-slate-500 mb-4">
                Last generation: <span className="font-medium">{queueStatus.last_daily_generation.date}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <ActionButton
                onClick={handleProcessQueue}
                loading={actionLoading.processQueue}
                icon={<Play size={18} weight="fill" />}
                label="Process Queue Now"
                color="blue"
                testId="process-queue-btn"
              />
              <ActionButton
                onClick={handleGenerateDaily}
                loading={actionLoading.generateDaily}
                icon={<VideoCamera size={18} weight="duotone" />}
                label="Generate Daily Content"
                color="emerald"
                testId="generate-daily-btn"
              />
            </div>
          </div>
        </div>

        {/* Media API Settings */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="settings-section">
          <div className="p-4 md:p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Gear size={20} weight="duotone" className="text-slate-500" />
              Media API Settings
            </h2>
          </div>
          
          <div className="p-4 md:p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Pexels API Key
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="password"
                  value={pexelsKey}
                  onChange={(e) => setPexelsKey(e.target.value)}
                  placeholder={settings?.pexels_api_key_configured ? `Current: ${settings.pexels_api_key_masked}` : 'Enter Pexels API key'}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  data-testid="pexels-api-input"
                />
                <button
                  onClick={handleSaveSettings}
                  disabled={actionLoading.saveSettings}
                  className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  data-testid="save-settings-btn"
                >
                  {actionLoading.saveSettings ? 'Saving...' : 'Save Key'}
                </button>
              </div>
              {settings?.pexels_api_key_configured && (
                <p className="mt-2 text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle size={14} weight="fill" />
                  Pexels API key is configured
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Email Drip Campaigns */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="drip-section">
          <div className="p-4 md:p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <EnvelopeSimple size={20} weight="duotone" className="text-purple-500" />
              Email Drip Campaigns
            </h2>
          </div>
          
          <div className="p-4 md:p-6">
            {/* Drip Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <DripStatCard
                label="Day 3 Follow-up"
                sent={dripStats?.total_sent?.day_3_followup || 0}
                eligible={dripStats?.eligible_users?.day_3_followup || 0}
              />
              <DripStatCard
                label="Day 7 Follow-up"
                sent={dripStats?.total_sent?.day_7_followup || 0}
                eligible={dripStats?.eligible_users?.day_7_followup || 0}
              />
              <DripStatCard
                label="Inactive Reminder"
                sent={dripStats?.total_sent?.inactive_user_reminder || 0}
                eligible={dripStats?.eligible_users?.inactive_user_reminder || 0}
              />
            </div>

            {dripStats?.last_campaign_run && (
              <p className="text-sm text-slate-500 mb-4">
                Last run: {new Date(dripStats.last_campaign_run).toLocaleString()}
              </p>
            )}

            <ActionButton
              onClick={handleRunDripCampaigns}
              loading={actionLoading.runDrip}
              icon={<EnvelopeSimple size={18} weight="duotone" />}
              label="Run Drip Campaigns Now"
              color="purple"
              testId="run-drip-btn"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ label, value, icon, color }) => {
  const bgColors = {
    amber: 'bg-amber-50',
    blue: 'bg-blue-50',
    emerald: 'bg-emerald-50',
    red: 'bg-red-50'
  };
  
  return (
    <div className={`${bgColors[color]} rounded-lg p-3 md:p-4`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
};

const DripStatCard = ({ label, sent, eligible }) => (
  <div className="bg-purple-50 rounded-lg p-4">
    <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
    <div className="flex justify-between items-end">
      <div>
        <div className="text-xl font-bold text-slate-900">{sent}</div>
        <div className="text-xs text-slate-500">sent</div>
      </div>
      <div className="text-right">
        <div className="text-lg font-medium text-purple-600">{eligible}</div>
        <div className="text-xs text-slate-500">eligible</div>
      </div>
    </div>
  </div>
);

const ActionButton = ({ onClick, loading, icon, label, color, testId }) => {
  const colors = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    purple: 'bg-purple-600 hover:bg-purple-700'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`flex items-center justify-center gap-2 px-5 py-2.5 ${colors[color]} text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
      data-testid={testId}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : (
        icon
      )}
      {label}
    </button>
  );
};

const RunwayStatusCard = ({ status, onRefresh }) => {
  const health = status?.health || 'unavailable';
  const palette = {
    healthy: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Healthy' },
    low_credits: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Low credits' },
    error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', label: 'Out of credits' },
    unavailable: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', dot: 'bg-slate-400', label: 'Unavailable' },
  };
  const p = palette[health] || palette.unavailable;
  const lastRun = status?.last_scheduler_run || {};
  const fmtIso = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const lastRunLabel = {
    idle: 'Idle — no pending videos',
    skipped_low_credits: 'Skipped (low credits)',
    processed: 'Processed',
    error: 'Error',
  }[lastRun.status] || 'No runs yet';

  return (
    <div
      className={`rounded-xl border ${p.border} ${p.bg} shadow-sm mb-6`}
      data-testid="runway-status-card"
    >
      <div className="p-4 md:p-6 border-b border-white/50 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <FilmSlate size={20} weight="duotone" className="text-indigo-600" />
          Runway Status
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2" data-testid="runway-health-indicator">
            <span className={`w-2.5 h-2.5 rounded-full ${p.dot}`}></span>
            <span className={`text-sm font-medium ${p.text}`}>{p.label}</span>
          </div>
          <button onClick={onRefresh} className="text-slate-500 hover:text-slate-700 p-1.5" title="Refresh">
            <ArrowClockwise size={18} />
          </button>
        </div>
      </div>

      <div className="p-4 md:p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 border border-slate-100" data-testid="runway-credit-balance">
            <div className="text-xs text-slate-500 mb-1">Credit Balance</div>
            <div className="text-xl font-bold text-slate-900">
              {status?.balance ?? '—'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-100" data-testid="runway-videos-remaining">
            <div className="text-xs text-slate-500 mb-1">Videos Remaining</div>
            <div className="text-xl font-bold text-slate-900">
              {status?.videos_remaining ?? '—'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-100" data-testid="runway-queue-pending">
            <div className="text-xs text-slate-500 mb-1">Queued Jobs</div>
            <div className="text-xl font-bold text-slate-900">
              {(status?.queue?.pending ?? 0) + (status?.queue?.processing ?? 0)}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {status?.queue?.processing ?? 0} processing
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-100" data-testid="runway-scheduler-next">
            <div className="text-xs text-slate-500 mb-1">Next Scheduler Run</div>
            <div className="text-sm font-mono text-slate-800 truncate">
              {status?.next_run ? new Date(status.next_run).toLocaleTimeString() : '—'}
            </div>
            <div className={`text-[11px] mt-0.5 ${status?.scheduler_running ? 'text-emerald-600' : 'text-red-600'}`}>
              {status?.scheduler_running ? 'Scheduler running' : 'Scheduler stopped'}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 border border-slate-100 text-sm" data-testid="runway-last-run">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="text-slate-500 text-xs">Last run: </span>
              <span className="font-medium text-slate-800">{lastRunLabel}</span>
            </div>
            <span className="text-slate-400 text-xs font-mono">{fmtIso(lastRun.ran_at)}</span>
          </div>
          {lastRun.message && (
            <p className="text-slate-500 text-xs mt-1">{lastRun.message}</p>
          )}
        </div>

        {status?.health_message && health !== 'healthy' && (
          <p className={`text-xs mt-3 ${p.text}`} data-testid="runway-health-message">
            {status.health_message}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminOperationsPage;
